import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { useInView } from 'react-cool-inview';

import {
    useGetAllTracklogsQuery,
    useTriggerDebriefForTrackUUIDMutation,
} from 'ff/debrief/features/TracklogApi';
import { PAGE_SIZE, PAGE_HEIGHT } from 'ff/debrief/utils';
import TracklogListItem from 'ff/debrief/components/sidebar/TracklogListItem';
import DateDivider from 'ff/debrief/components/sidebar/DateDivider';
import GhostItem from 'ff/debrief/components/sidebar/GhostItem';
import { useCurrentTracklog } from 'ff/debrief/hooks';
import ItemDivider from 'ff/debrief/components/sidebar/ItemDivider';
import { isFeatureEnabled } from 'ff/common/ffAppUtils';

interface Props {
    page: number;
    startTimestamp: number;
    endTimestamp: number;
    fuzzySearchTerm: string;
}

const TracklogListPage: React.FC<Props> = props => {
    const isFirstPage = props.page === 0;
    const { tracklog: selectedTrack, setTracklog } = useCurrentTracklog();
    const [visible, setVisible] = React.useState(false);
    const [isPolling, setIsPolling] = React.useState(false);
    const pageQuery = useGetAllTracklogsQuery(
        {
            page: props.page,
            startTimestamp: props.startTimestamp,
            endTimestamp: props.endTimestamp,
            fuzzySearchTerm: props.fuzzySearchTerm,
        },
        { skip: !visible, pollingInterval: isPolling && 5000 }
    );
    const prevPageQuery = useGetAllTracklogsQuery(
        { page: props.page - 1 },
        {
            skip: !visible || isFirstPage,
        }
    );
    const [triggerAnalysis] = useTriggerDebriefForTrackUUIDMutation();
    const listIsLoaded = pageQuery.isSuccess;
    React.useEffect(() => {
        if (!isFeatureEnabled('debrief_features')) return undefined;
        // TODO replace placeholder license check
        if (listIsLoaded) {
            pageQuery.data.tracklogs.forEach(tl => {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                tl.analysisSummary === null && triggerAnalysis(tl.trackUuid);
            });
        }
    }, [listIsLoaded]);

    const isAwaitingAnalysis = pageQuery.data?.tracklogs.some(
        tl =>
            !(
                tl.analysisSummary?.status === 'COMPLETED' ||
                tl.analysisSummary?.status === 'ERROR'
            )
    );
    React.useEffect(() => {
        if (isFeatureEnabled('debrief_features') && isAwaitingAnalysis) {
            setIsPolling(true);
        } else {
            setIsPolling(false);
        }
    }, [isAwaitingAnalysis]);
    // this observe
    const { observe } = useInView({
        rootMargin: '150px 0px',
        onEnter: () => {
            setVisible(true);
        },
    });

    const handleClick = (tracklogId: string) => {
        setTracklog(tracklogId);
    };

    if (pageQuery.data && (prevPageQuery.data || isFirstPage)) {
        const listItems: JSX.Element[] = [];
        pageQuery.data.tracklogs.forEach((tl, idx, arr) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let prevEntry: any;
            if (isFirstPage && idx === 0) {
                prevEntry = null;
            } else if (!isFirstPage && idx === 0) {
                prevEntry = prevPageQuery.data.tracklogs.at(-1);
            } else {
                prevEntry = arr[idx - 1];
            }
            const currMonth = new Date(tl.timestampStart).getMonth();
            const prevMonth = new Date(prevEntry?.timestampStart).getMonth();
            if (currMonth !== prevMonth) {
                listItems.push(
                    <DateDivider
                        key={tl.timestampStart}
                        dateString={tl.timestampStart}
                    />
                );
            } else {
                listItems.push(<ItemDivider key={tl.trackUuid + 'div'} />);
            }
            listItems.push(
                <TracklogListItem
                    key={tl.trackUuid}
                    selected={tl.trackUuid === selectedTrack.trackUuid}
                    onClick={handleClick}
                    {...tl}
                />
            );
        });
        return <React.Fragment>{listItems}</React.Fragment>;
    } else if (
        pageQuery.isUninitialized ||
        prevPageQuery.isUninitialized ||
        pageQuery.isFetching ||
        prevPageQuery.isFetching
    ) {
        return (
            <PageContainer ref={observe} height={`${PAGE_HEIGHT}px`}>
                {_.range(PAGE_SIZE).map(idx => (
                    <GhostItem key={idx} />
                ))}
            </PageContainer>
        );
    }
};

const PageContainer = styled.div<{ height: string }>`
    height: ${props => props.height};
`;

export default TracklogListPage;
